// Server-side environment configuration
// This file only uses process.env for Node.js environment

interface ServerEnvironmentConfig {
  // Firebase Service Account
  firebase: {
    projectId: string;
    clientEmail: string;
    privateKey: string;
    databaseUrl?: string;
  };

  // Supabase Service Role
  supabase: {
    url: string;
    serviceRoleKey: string;
    jwtSecret: string;
  };

  // Cloudinary
  cloudinary: {
    cloudName: string;
    apiKey: string;
    apiSecret: string;
  };

  // Email Service
  resend: {
    apiKey?: string;
    fromEmail: string;
  };

  // Payment Processors
  paypal?: {
    clientId?: string;
    clientSecret?: string;
    webhookId?: string;
  };

  mpesa: {
    consumerKey?: string;
    consumerSecret?: string;
    shortcode: string;
    passkey?: string;
    phoneNumber?: string;
  };

  // WhatsApp Business API
  whatsapp: {
    accessToken?: string;
    phoneNumberId?: string;
    apiVersion: string;
    webhookVerifyToken?: string;
    appSecret?: string;
  };

  // Application
  app: {
    port: number;
    nodeEnv: string;
    frontendUrl: string;
    corsOrigin: string[] | RegExp;
  };
}

// Validate server environment variables
const validateServerEnv = (): ServerEnvironmentConfig => {
  // For now, always use development defaults to get the server running
  // TODO: Implement proper environment variable validation for production
  console.log('🔧 Starting server in development mode with default configuration');

  // Parse CORS origins - dynamic based on environment
  let corsOrigins: string[] | RegExp;

  // Check if we're in development mode (default to development if not explicitly set to production)
  const isDevelopment = process.env.NODE_ENV !== 'production';

  if (isDevelopment) {
    // In development, accept any localhost origin (any port) - no hardcoding needed!
    corsOrigins = /^http:\/\/localhost(:\d+)?$/;
  } else if (process.env.CORS_ORIGINS) {
    // In production, use specific origins from env
    corsOrigins = process.env.CORS_ORIGINS.split(',').map(origin => origin.trim());
  } else {
    // Fallback to frontend URL
    corsOrigins = [process.env.FRONTEND_URL || 'http://localhost:3005'];
  }

  return {
    firebase: {
      projectId: process.env.FIREBASE_PROJECT_ID || 'development-project-id',
      clientEmail: process.env.FIREBASE_CLIENT_EMAIL || 'development@example.com',
      privateKey: (process.env.FIREBASE_PRIVATE_KEY || 'development-private-key').replace(/\\n/g, '\n'),
      ...(process.env.FIREBASE_DATABASE_URL && { databaseUrl: process.env.FIREBASE_DATABASE_URL })
    },

    supabase: {
      url: process.env.SUPABASE_URL || 'https://development.supabase.co',
      serviceRoleKey: process.env.SUPABASE_SERVICE_ROLE_KEY || 'development-service-role-key',
      jwtSecret: process.env.SUPABASE_JWT_SECRET || 'development-jwt-secret'
    },

    cloudinary: {
      cloudName: process.env.CLOUDINARY_CLOUD_NAME || 'development-cloud',
      apiKey: process.env.CLOUDINARY_API_KEY || 'development-key',
      apiSecret: process.env.CLOUDINARY_API_SECRET || 'development-secret'
    },

    resend: {
      ...(process.env.RESEND_API_KEY && { apiKey: process.env.RESEND_API_KEY }),
      fromEmail: process.env.RESEND_FROM_EMAIL || 'noreply@swaysuite.com'
    },

    paypal: {
      ...(process.env.PAYPAL_CLIENT_ID && { clientId: process.env.PAYPAL_CLIENT_ID }),
      ...(process.env.PAYPAL_CLIENT_SECRET && { clientSecret: process.env.PAYPAL_CLIENT_SECRET }),
      ...(process.env.PAYPAL_WEBHOOK_ID && { webhookId: process.env.PAYPAL_WEBHOOK_ID })
    },

    mpesa: {
      ...(process.env.MPESA_CONSUMER_KEY && { consumerKey: process.env.MPESA_CONSUMER_KEY }),
      ...(process.env.MPESA_CONSUMER_SECRET && { consumerSecret: process.env.MPESA_CONSUMER_SECRET }),
      shortcode: process.env.MPESA_SHORTCODE || '174379',
      ...(process.env.MPESA_PASSKEY && { passkey: process.env.MPESA_PASSKEY }),
      ...(process.env.MPESA_PHONE_NUMBER && { phoneNumber: process.env.MPESA_PHONE_NUMBER })
    },

    whatsapp: {
      ...(process.env.WHATSAPP_ACCESS_TOKEN && { accessToken: process.env.WHATSAPP_ACCESS_TOKEN }),
      ...(process.env.WHATSAPP_PHONE_NUMBER_ID && { phoneNumberId: process.env.WHATSAPP_PHONE_NUMBER_ID }),
      apiVersion: process.env.WHATSAPP_API_VERSION || 'v18.0',
      ...(process.env.WHATSAPP_WEBHOOK_VERIFY_TOKEN && { webhookVerifyToken: process.env.WHATSAPP_WEBHOOK_VERIFY_TOKEN }),
      ...(process.env.WHATSAPP_APP_SECRET && { appSecret: process.env.WHATSAPP_APP_SECRET })
    },

    app: {
      port: parseInt(process.env.PORT || process.env.API_PORT || '3002', 10),
      nodeEnv: process.env.NODE_ENV || 'development',
      frontendUrl: process.env.FRONTEND_URL || 'http://localhost:3005',
      corsOrigin: corsOrigins
    }
  };
};

// Export the validated environment configuration
export const serverConfig = validateServerEnv();

// Helper to check if we're in development
export const isServerDev = serverConfig.app.nodeEnv === 'development';

// Helper to check if we're in production
export const isServerProd = serverConfig.app.nodeEnv === 'production';

// Helper to check if a service is properly configured
export const isServerServiceConfigured = (service: keyof Omit<ServerEnvironmentConfig, 'app'>): boolean => {
  const serviceConfig = serverConfig[service];

  switch (service) {
    case 'firebase':
      const firebaseConfig = serviceConfig as ServerEnvironmentConfig['firebase'];
      return !!(firebaseConfig.projectId && firebaseConfig.clientEmail && firebaseConfig.privateKey);
    case 'supabase':
      const supabaseConfig = serviceConfig as ServerEnvironmentConfig['supabase'];
      return !!(supabaseConfig.url && supabaseConfig.serviceRoleKey && supabaseConfig.jwtSecret);
    case 'cloudinary':
      const cloudinaryConfig = serviceConfig as ServerEnvironmentConfig['cloudinary'];
      return !!(cloudinaryConfig.cloudName && cloudinaryConfig.apiKey && cloudinaryConfig.apiSecret);
    case 'resend':
      const resendConfig = serviceConfig as ServerEnvironmentConfig['resend'];
      return !!resendConfig.apiKey;
    case 'paypal':
      const paypalConfig = serviceConfig as ServerEnvironmentConfig['paypal'];
      return !!(paypalConfig?.clientId && paypalConfig?.clientSecret);
    case 'mpesa':
      const mpesaConfig = serviceConfig as ServerEnvironmentConfig['mpesa'];
      return !!(mpesaConfig.consumerKey && mpesaConfig.consumerSecret);
    case 'whatsapp':
      const whatsappConfig = serviceConfig as ServerEnvironmentConfig['whatsapp'];
      return !!(whatsappConfig.accessToken && whatsappConfig.phoneNumberId);
    default:
      return true;
  }
};

// Log configuration status
const corsOriginDisplay = Array.isArray(serverConfig.app.corsOrigin)
  ? serverConfig.app.corsOrigin.join(', ')
  : serverConfig.app.corsOrigin.toString();

console.log(
  '🔧 Server Environment Configuration:\n' +
  `  - Environment: ${serverConfig.app.nodeEnv}\n` +
  `  - Port: ${serverConfig.app.port}\n` +
  `  - Firebase: ${isServerServiceConfigured('firebase') ? '✅ Configured' : '❌ Missing vars'}\n` +
  `  - Supabase: ${isServerServiceConfigured('supabase') ? '✅ Configured' : '❌ Missing vars'}\n` +
  `  - Cloudinary: ${isServerServiceConfigured('cloudinary') ? '✅ Configured' : '❌ Missing vars'}\n` +
  `  - Resend: ${isServerServiceConfigured('resend') ? '✅ Configured' : '❌ Missing vars'}\n` +
  `  - PayPal: ${isServerServiceConfigured('paypal') ? '✅ Configured' : '⚠️ Not configured'}\n` +
  `  - M-Pesa: ${isServerServiceConfigured('mpesa') ? '✅ Configured' : '⚠️ Not configured'}\n` +
  `  - WhatsApp: ${isServerServiceConfigured('whatsapp') ? '✅ Configured' : '⚠️ Not configured'}\n` +
  `  - Frontend URL: ${serverConfig.app.frontendUrl}\n` +
  `  - CORS Origins: ${corsOriginDisplay}`
);